/**
 * Customer Login Multi-Step
 *
 * Handles the multi-step login/register flow:
 *  Step "init"     → Email capture + social login entry points
 *  Step "register" → Create account (new user detected via Storefront API)
 *  Step "guest"    → Guest order lookup
 *  Step "login"    → Sign in (existing user detected via Storefront API)
 *  Step "recover"  → Recover password
 *
 * Email detection technique: call customerCreate with a deliberately invalid
 * password and inspect the validation error code:
 *   TAKEN      → email exists → go to login
 *   TOO_SHORT  → email does not exist → go to register
 */

const customerLoginText = (key) => window.customerLoginStrings?.[key] || '';
// Los teclados móviles pueden añadir espacios no separables o de ancho cero que invalidan el pattern del email
const cleanEmail = (value) => (value || '').replace(/[\s\u200B-\u200D\u2060\uFEFF]/g, '');

class CustomerLoginSteps {
  constructor(container) {
    this.container = container;
    this.storefrontToken = container.dataset.storefrontToken || '';
    this.shopUrl = container.dataset.shopUrl || window.location.hostname;
    this.customerProfileUrl = container.dataset.customerProfileUrl || '/apps/customer-profile';
    this.guestOrderPageUrl = container.dataset.guestOrderPageUrl || '/pages/pedido';

    this.steps = {
      init: container.querySelector('[data-step="init"]'),
      register: container.querySelector('[data-step="register"]'),
      guest: container.querySelector('[data-step="guest"]'),
      login: container.querySelector('[data-step="login"]'),
      recover: container.querySelector('[data-step="recover"]'),
      'reset-password': container.querySelector('[data-step="reset-password"]'),
      'activate-account': container.querySelector('[data-step="activate-account"]'),
    };

    this.init();
  }

  init() {
    this._detectAndShowInitialStep();
    this._bindActions();
    this._bindRegisterForm();
    this._bindPasswordHints();
    this._initPasswordToggles();
  }

  /* ----------------------------------------
     Step detection on page load
  ---------------------------------------- */
  _detectAndShowInitialStep() {
    // Solo aplicar la lógica multi-step de login si están los pasos típicos.
    // En páginas standalone (register, reset-password, activate-account) sólo
    // hay un step visible y no debemos ocultarlo.
    const hasMultiStep =
      this.steps.init || this.steps.login || this.steps.recover || this.steps.guest;
    if (!hasMultiStep) {
      return;
    }

    const guestParam = new URLSearchParams(window.location.search).get('guest');
    const initialStep =
      guestParam === 'true' ? 'guest' : this.container.dataset.initialStep;
    if (initialStep && initialStep !== 'init' && this.steps[initialStep]) {
      this._showStep(initialStep);
      return;
    }

    // Login errors: Shopify re-renders the page with errors inside the login step
    const hasLoginErrors =
      this.steps.login &&
      (this.steps.login.querySelector('.field.has-error') ||
        this.steps.login.querySelector('[class*="color-red"]'));

    // Recover success: Shopify re-renders after successful recover_customer_password
    const hasRecoverSuccess =
      this.steps.recover && this.steps.recover.querySelector('[data-recover-success]');

    // Recover errors
    const hasRecoverErrors =
      this.steps.recover && this.steps.recover.querySelector('.field.has-error');

    if (hasLoginErrors) {
      this._showStep('login');
      // Pre-fill email from the hidden or visible email field if present
      const emailField = this.steps.login.querySelector('#CustomerEmail');
      if (emailField && emailField.value) {
        this._storeEmail(emailField.value);
      }
    } else if (hasRecoverSuccess || hasRecoverErrors) {
      this._showStep('recover');
    } else {
      this._showStep('init');
    }
  }

  /* ----------------------------------------
     Show a step by name
  ---------------------------------------- */
  _showStep(name) {
    Object.entries(this.steps).forEach(([key, el]) => {
      if (!el) return;
      if (key === name) {
        el.removeAttribute('hidden');
        el.focus && el.focus();
      } else {
        el.setAttribute('hidden', '');
      }
    });

    // Scroll to top of the form column on mobile
    if (window.innerWidth < 1024) {
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (name === 'recover') this._primeRecoverForm();
  }

  // Shopify prepara su protección anti-bots al enfocar un campo; sin eso el primer envío en móvil se pierde
  _primeRecoverForm() {
    const field = this.steps.recover?.querySelector('#RecoverEmail');
    if (!field || this.recoverPrimed) return;
    this.recoverPrimed = true;
    ['focus', 'focusin', 'blur', 'focusout'].forEach((type) => {
      field.dispatchEvent(new FocusEvent(type, { bubbles: type === 'focusin' || type === 'focusout' }));
    });
  }

  /* ----------------------------------------
     Email storage helpers (across steps)
  ---------------------------------------- */
  _storeEmail(email) {
    try {
      sessionStorage.setItem('lola_login_email', cleanEmail(email));
    } catch (_) {}
  }

  _getStoredEmail() {
    try {
      return cleanEmail(sessionStorage.getItem('lola_login_email'));
    } catch (_) {
      return '';
    }
  }

  _propagateEmail(email) {
    email = cleanEmail(email);
    this._storeEmail(email);

    // Pre-fill all relevant email fields
    const fields = [
      this.steps.register && this.steps.register.querySelector('#RegisterEmail'),
      this.steps.login && this.steps.login.querySelector('#CustomerEmail'),
      this.steps.guest && this.steps.guest.querySelector('#GuestEmail'),
      this.steps.recover && this.steps.recover.querySelector('#RecoverEmail'),
    ];
    fields.forEach((field) => {
      if (field && !field.value) field.value = email;
    });
  }

  /* ----------------------------------------
     Event bindings
  ---------------------------------------- */
  _bindActions() {
    // Init step: "Continuar" button
    const checkBtn = this.steps.init && this.steps.init.querySelector('[data-action="check-email"]');
    if (checkBtn) {
      checkBtn.addEventListener('click', () => this._handleEmailCheck(checkBtn));
    }

    // Init step: Enter key on email input
    const initEmailInput = this.steps.init && this.steps.init.querySelector('#InitEmail');
    if (initEmailInput) {
      initEmailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._handleEmailCheck(checkBtn);
        }
      });
    }

    // Init step: "Accede como invitada"
    const guestBtn = this.steps.init && this.steps.init.querySelector('[data-action="goto-guest"]');
    if (guestBtn) {
      guestBtn.addEventListener('click', () => {
        const email = this.steps.init.querySelector('#InitEmail')?.value || '';
        if (email) this._propagateEmail(email);
        this._showStep('guest');
      });
    }

    // Guest step: "VOLVER"
    const backBtn =
      this.steps.guest && this.steps.guest.querySelector('[data-action="back-from-guest"]');
    if (backBtn) {
      backBtn.addEventListener('click', () => this._showStep('init'));
    }

    // Guest step: "Crear cuenta" → mostrar step de registro
    const guestToRegisterBtn =
      this.steps.guest && this.steps.guest.querySelector('[data-action="goto-register-from-guest"]');
    if (guestToRegisterBtn) {
      guestToRegisterBtn.addEventListener('click', () => {
        const email = this.steps.guest.querySelector('#GuestEmail')?.value || this._getStoredEmail();
        if (email) this._propagateEmail(email);
        this._showStep('register');
      });
    }

    // Login step: "¿Has olvidado tu contraseña?"
    const recoverBtn =
      this.steps.login && this.steps.login.querySelector('[data-action="goto-recover"]');
    if (recoverBtn) {
      recoverBtn.addEventListener('click', () => {
        // Pre-fill recover email from login email field
        const loginEmail =
          this.steps.login.querySelector('#CustomerEmail')?.value || this._getStoredEmail();
        const recoverEmailField =
          this.steps.recover && this.steps.recover.querySelector('#RecoverEmail');
        if (recoverEmailField && loginEmail) {
          recoverEmailField.value = cleanEmail(loginEmail);
        }
        this._showStep('recover');
      });
    }

    const recoverEmailField = this.steps.recover && this.steps.recover.querySelector('#RecoverEmail');
    const recoverSubmitBtn = this.steps.recover && this.steps.recover.querySelector('button[type="submit"]');
    const normalizeRecoverEmail = () => {
      if (recoverEmailField) recoverEmailField.value = cleanEmail(recoverEmailField.value);
    };
    recoverEmailField?.addEventListener('blur', normalizeRecoverEmail);
    // El click llega antes que la validación nativa; en submit ya sería tarde
    recoverSubmitBtn?.addEventListener('click', normalizeRecoverEmail);

    const recoverForm = recoverEmailField?.form;
    let recoverFieldTouched = false;
    recoverEmailField?.addEventListener('focus', (event) => {
      if (event.isTrusted) recoverFieldTouched = true;
    });
    recoverSubmitBtn?.addEventListener('click', (event) => {
      if (recoverFieldTouched || !recoverForm) return;
      event.preventDefault();
      recoverFieldTouched = true;
      recoverEmailField.focus({ preventScroll: true });
      recoverEmailField.blur();
      setTimeout(() => {
        if (recoverForm.requestSubmit) recoverForm.requestSubmit(recoverSubmitBtn);
        else recoverSubmitBtn.click();
      }, 500);
    });

    // Register step: "¿Ya tienes cuenta? Accede aquí"
    const toLoginBtn =
      this.steps.register && this.steps.register.querySelector('[data-action="goto-login"]');
    if (toLoginBtn) {
      toLoginBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const email =
          this.steps.register.querySelector('#RegisterEmail')?.value || this._getStoredEmail();
        if (email) this._propagateEmail(email);
        this._showStep('login');
      });
    }

    // Guest: search button - open order page with temporary request data
    const guestSearchBtn =
      this.steps.guest && this.steps.guest.querySelector('#guest-search-btn');
    if (guestSearchBtn) {
      guestSearchBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const emailInput = this.steps.guest.querySelector('#GuestEmail');
        const orderInput = this.steps.guest.querySelector('#GuestOrderNumber');
        const email = emailInput?.value.trim() || '';
        const orderName = orderInput?.value.trim() || '';
        const setGuestFieldError = (input, message) => {
          const field = input?.closest('[data-input-wrapper]');
          const errorMessage = field?.querySelector('[data-message]');
          if (!field || !errorMessage) return;
          field.classList.toggle('has-error', Boolean(message));
          errorMessage.classList.toggle('hidden', !message);
          errorMessage.textContent = message || '';
          input.setAttribute('aria-invalid', message ? 'true' : 'false');
        };
        const emailIsValid = Boolean(email) && this._isValidEmail(email);
        const orderIsValid = Boolean(orderName);

        setGuestFieldError(emailInput, emailIsValid ? '' : emailInput?.dataset.errorMessage);
        setGuestFieldError(orderInput, orderIsValid ? '' : orderInput?.dataset.errorMessage);

        if (!emailIsValid || !orderIsValid) {
          (emailIsValid ? orderInput : emailInput)?.focus();
          return;
        }

        try {
          const createdAt = Date.now();
          const expiresAt = createdAt + (2 * 60 * 60 * 1000);
          const nonce = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${createdAt}-${Math.random()}`;
          const hash = await this._createGuestOrderHash(`${email.trim().toLowerCase()}|${orderName.trim().toLowerCase()}|${createdAt}|${nonce}`);
          const orderPageUrl = new URL(this.guestOrderPageUrl, window.location.href);
          const orderPageHashParams = new URLSearchParams(orderPageUrl.hash.replace(/^#/, ''));

          sessionStorage.setItem('lola_guest_order_request', JSON.stringify({
            email,
            order: orderName,
            hash,
            nonce,
            createdAt,
            expiresAt,
          }));

          orderPageHashParams.set('order', hash);
          orderPageUrl.hash = orderPageHashParams.toString();
          window.location.assign(orderPageUrl.toString());
        } catch (_) {}
      });
    }
  }

  /* ----------------------------------------
     Email check via Storefront API
  ---------------------------------------- */
  async _handleEmailCheck(btn) {
    const emailInput = this.steps.init && this.steps.init.querySelector('#InitEmail');
    if (!emailInput) return;

    const email = emailInput.value.trim();

    // Basic validation
    if (!email || !this._isValidEmail(email)) {
      emailInput.closest('[data-input-wrapper]')?.classList.add('has-error');
      return;
    }
    emailInput.closest('[data-input-wrapper]')?.classList.remove('has-error');

    // Show loading state
    if (btn) {
      btn.dataset.loading = 'true';
      btn.disabled = true;
    }
    this._clearApiError();

    try {
      const emailExists = await this._checkEmailExists(email);
      this._propagateEmail(email);

      if (emailExists === 'disabled') {
        await this._checkLegacyCustomer(email, true).catch(() => false);
        this._showLegacyResetMessage(email);
        return;
      }

      if (!emailExists) {
        this._showStep('register');
        return;
      }

      const passwordResetSent = await this._checkLegacyCustomer(email);
      if (passwordResetSent) {
        this._showLegacyResetMessage(email);
        return;
      }

      this._showStep('login');
    } catch (err) {
      // On network/API error, default to login (safe fallback)
      console.warn('[CustomerLogin] Email check failed, defaulting to login:', err);
      this._propagateEmail(email);
      this._showStep('login');
    } finally {
      if (btn) {
        delete btn.dataset.loading;
        btn.disabled = false;
      }
    }
  }

  async _checkLegacyCustomer(email, inviteAlreadySent = false) {
    const response = await fetch(this.customerProfileUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'check_legacy_customer', email, invite_already_sent: inviteAlreadySent }),
    });

    if (!response.ok) throw new Error(`Legacy customer check failed: HTTP ${response.status}`);
    const data = await response.json();
    return data?.passwordResetSent === true;
  }

  _showLegacyResetMessage(email) {
    const init = this.steps.init;
    const initBody = this.steps.init?.querySelector('.form-customer__body');
    if (!initBody) return;
    init?.querySelector('[data-legacy-reset-title]')?.removeAttribute('hidden');
    init?.querySelector('.form-customer__head .form-customer__title:not([data-legacy-reset-title])')?.setAttribute('hidden', '');
    initBody.querySelectorAll('.form-row').forEach((row) => {
      row.hidden = true;
    });
    const message = initBody.querySelector('[data-legacy-reset-success]');
    if (message) {
      message.textContent = message.dataset.template.replace('__EMAIL__', this._maskEmail(email));
      message.removeAttribute('hidden');
    }
    initBody.querySelector('[data-legacy-reset-action]')?.removeAttribute('hidden');
  }

  _maskEmail(email) {
    const [localPart, domain] = email.split('@');
    return `${localPart.charAt(0)}*******@${domain}`;
  }

  /**
   * Check if an email exists as a Shopify customer.
   *
  * Technique: attempt customerCreate with an invalid password.
  *  - TAKEN     → email registered → return true
  *  - TOO_SHORT → email not registered, password rejected before creation → return false
  *  - CUSTOMER_DISABLED → account not activated; Shopify emails the activation invite → return 'disabled'
   *  - Any other error        → throw (caller defaults to login)
   */
  async _checkEmailExists(email) {
    if (!this.storefrontToken) {
      // No token configured: default to login step (cannot determine)
      return true;
    }

    const query = /* graphql */ `
      mutation customerCreate($input: CustomerCreateInput!) {
        customerCreate(input: $input) {
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `;

    const response = await fetch(
      `https://${this.shopUrl}/api/2024-01/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query,
          variables: {
            input: {
              email,
              password: 'x',
            },
          },
        }),
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    const errors =
      data?.data?.customerCreate?.customerUserErrors || [];

    if (errors.some((e) => e.code === 'CUSTOMER_DISABLED')) return 'disabled';
    if (errors.some((e) => e.code === 'TAKEN')) return true;
    if (errors.some((e) => e.code === 'TOO_SHORT')) return false;

    throw new Error(`Unexpected customerCreate response: ${JSON.stringify(errors)}`);
  }

  _bindRegisterForm() {
    const registerForm = this.steps.register && this.steps.register.querySelector('form');
    if (!registerForm) return;

    const credentialsStep = this.steps.register.querySelector('[data-register-step="credentials"]');
    const detailsStep = this.steps.register.querySelector('[data-register-step="details"]');
    const nextButton = this.steps.register.querySelector('[data-action="register-next"]');
    const emailInput = this.steps.register.querySelector('#RegisterEmail');
    const passwordInput = this.steps.register.querySelector('#RegisterPassword');
    const birthDateInput = this.steps.register.querySelector('[data-birth-date-input]');
    const birthDateNoteInput = this.steps.register.querySelector('[data-birth-date-note]');
    const phoneInput = this.steps.register.querySelector('[data-register-phone]');
    const firstNameInput = this.steps.register.querySelector('#RegisterFirstName');
    const lastNameInput = this.steps.register.querySelector('#RegisterLastName');
    const readonlyEmailInput = this.steps.register.querySelector('[data-register-email-readonly]');
    const consentInputs = this.steps.register.querySelectorAll('.form-customer__consent-control input[type="checkbox"]');
    const resetConsentInputs = () => {
      consentInputs.forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
      });
    };
    const resetBirthDateInputs = () => {
      if (birthDateInput) birthDateInput.value = '';
    };

    resetConsentInputs();
    resetBirthDateInputs();
    window.addEventListener('pageshow', () => {
      resetConsentInputs();
      resetBirthDateInputs();
    });

    birthDateInput?.addEventListener('focus', () => {
      if (!birthDateInput.value) {
        birthDateInput.placeholder = birthDateInput.dataset.birthDateFormat || 'DD/MM/YYYY';
      }
    });

    birthDateInput?.addEventListener('blur', () => {
      if (!birthDateInput.value) {
        birthDateInput.placeholder = birthDateInput.dataset.birthDateFormat || 'DD/MM/YYYY';
      }
    });

    birthDateInput?.addEventListener('input', () => {
      const rawValue = birthDateInput.value.replace(/\/$/, '');
      const digits = rawValue.replace(/\D/g, '').slice(0, 8);
      const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
      const format = birthDateInput.dataset.birthDateFormat || 'DD/MM/YYYY';
      const separator = format.match(/[./-]/)?.[0] || '/';
      birthDateInput.value = parts.join(separator);

    });

    const setFieldError = (input, message) => {
      const field = input?.closest('[data-input-wrapper]');
      const errorMessage = field?.querySelector('[data-message]');
      if (!field || !errorMessage) return;
      field.classList.toggle('has-error', Boolean(message));
      errorMessage.classList.toggle('hidden', !message);
      errorMessage.textContent = message || '';
      errorMessage.toggleAttribute('role', Boolean(message));
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    };
    const setInputError = (input, hasError) => {
      const field = input?.closest('[data-input-wrapper]');
      const errorMessage = field?.querySelector('[data-message]');
      if (!field) return;
      field.classList.toggle('has-error', hasError);
      input.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      errorMessage?.classList.add('hidden');
      if (errorMessage) errorMessage.textContent = '';
    };

    const getErrorMessage = (input, fallback) => input?.dataset.errorMessage || fallback;
    const validateEmailField = () => {
      const isValid = Boolean(emailInput?.value.trim()) && this._isValidEmail(emailInput.value.trim());
      setFieldError(emailInput, isValid ? '' : customerLoginText('emailError'));
      return isValid;
    };
    const validatePasswordField = () => {
      const password = passwordInput?.value || '';
      const isValid = password.length >= 8 && password.length <= 30 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
      setFieldError(passwordInput, isValid ? '' : customerLoginText('passwordError'));
      return isValid;
    };
    const isValidPhone = (value) => {
      const phone = value.trim();
      if (!phone) return true;
      return /^[+()0-9\s.-]+$/.test(phone) && phone.replace(/\D/g, '').length >= 9 && phone.replace(/\D/g, '').length <= 15;
    };
    const isValidBirthDate = (value) => {
      const date = value.trim();
      if (!date) return true;
      const format = birthDateInput?.dataset.birthDateFormat || 'DD/MM/YYYY';
      const dateParts = date.split(/[./-]/);
      const formatParts = format.split(/[./-]/);
      if (dateParts.length !== 3 || formatParts.length !== 3) return false;
      const dayIndex = formatParts.indexOf('DD');
      const monthIndex = formatParts.indexOf('MM');
      const yearIndex = formatParts.findIndex((part) => part === 'YYYY' || part === 'AAAA');
      if (dayIndex < 0 || monthIndex < 0 || yearIndex < 0) return false;
      const day = Number(dateParts[dayIndex]);
      const month = Number(dateParts[monthIndex]);
      const year = Number(dateParts[yearIndex]);
      if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
      const today = new Date();
      const parsed = new Date(year, month - 1, day);
      return year >= 1900 && parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day && parsed <= today;
    };
    const validatePhoneField = () => {
      if (!phoneInput || isValidPhone(phoneInput.value)) {
        if (phoneInput) setFieldError(phoneInput, '');
        return true;
      }
      setFieldError(phoneInput, getErrorMessage(phoneInput, customerLoginText('phoneError')));
      return false;
    };
    const validateBirthDateField = () => {
      if (!birthDateInput || isValidBirthDate(birthDateInput.value)) {
        if (birthDateInput) setFieldError(birthDateInput, '');
        return true;
      }
      setFieldError(birthDateInput, getErrorMessage(birthDateInput, customerLoginText('birthDateError')));
      return false;
    };
    const setConsentError = (consentInput, hasError) => {
      const consentRow = consentInput?.closest('.form-customer__consent');
      const errorMessage = consentRow?.querySelector('.field__message');
      consentRow?.classList.toggle('has-error', hasError);
      consentInput?.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      errorMessage?.classList.toggle('hidden', !hasError);
      if (hasError) errorMessage?.setAttribute('role', 'alert');
    };
    const validateAllRegisterFields = () => {
      const emailField = this.steps.register.querySelector('#RegisterEmail');
      const passwordField = this.steps.register.querySelector('#RegisterPassword');
      const privacyConsent = this.steps.register.querySelector('[data-required-consent] input');
      const password = passwordField?.value || '';
      const passwordValid = password.length >= 8 && password.length <= 30 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
      const results = [
        [emailField, emailField ? this._isValidEmail(emailField.value.trim()) : false, customerLoginText('emailError')],
        [passwordField, passwordValid, customerLoginText('passwordError')],
        [firstNameInput, Boolean(firstNameInput?.value.trim()), customerLoginText('firstNameError')],
        [lastNameInput, Boolean(lastNameInput?.value.trim()), customerLoginText('lastNameError')],
      ];
      let firstInvalid = null;
      results.forEach(([input, isValid, message]) => {
        if (input === firstNameInput || input === lastNameInput) {
          setInputError(input, !isValid);
        } else {
          setFieldError(input, isValid ? '' : message);
        }
        if (!isValid && !firstInvalid) firstInvalid = input;
      });
      if (!validatePhoneField() && !firstInvalid) firstInvalid = phoneInput;
      if (!validateBirthDateField() && !firstInvalid) firstInvalid = birthDateInput;
      if (privacyConsent && !privacyConsent.checked) {
        setConsentError(privacyConsent, true);
        if (!firstInvalid) firstInvalid = privacyConsent;
      } else {
        setConsentError(privacyConsent, false);
      }
      return { valid: !firstInvalid, firstInvalid };
    };

    if (nextButton && credentialsStep && detailsStep) {
      nextButton.addEventListener('click', () => {
        if (!validateEmailField()) {
          emailInput?.focus();
          return;
        }

        if (!validatePasswordField()) {
          passwordInput?.focus();
          return;
        }

        if (readonlyEmailInput) readonlyEmailInput.value = emailInput.value;
        credentialsStep.setAttribute('hidden', '');
        detailsStep.removeAttribute('hidden');
        detailsStep.querySelector('input')?.focus();
      });
    }

    registerForm.addEventListener('submit', (event) => {
      const validation = validateAllRegisterFields();
      if (!validation.valid) {
        event.preventDefault();
        validation.firstInvalid?.focus();
        return;
      }
      const emailField = this.steps.register.querySelector('#RegisterEmail');
      const passwordField = this.steps.register.querySelector('#RegisterPassword');
      const privacyConsent = this.steps.register.querySelector('[data-required-consent] input');
      const password = passwordField?.value || '';
      const isValidPassword =
        password.length >= 8 && password.length <= 30 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);

      if (emailField && !this._isValidEmail(emailField.value.trim())) {
        event.preventDefault();
        emailField.closest('[data-input-wrapper]')?.classList.add('has-error');
        emailField.focus();
        return;
      }

      if (!isValidPassword) {
        event.preventDefault();
        passwordField?.closest('[data-input-wrapper]')?.classList.add('has-error');
        passwordField?.focus();
        return;
      }

      const phoneValid = isValidPhone(phoneInput?.value || '');
      const birthDateValid = isValidBirthDate(birthDateInput?.value || '');
      if (!phoneValid || !birthDateValid) {
        event.preventDefault();
        if (!phoneValid) {
          validatePhoneField();
          phoneInput?.focus();
        } else {
          validateBirthDateField();
          birthDateInput?.focus();
        }
        return;
      }

      if (privacyConsent && !privacyConsent.checked) {
        event.preventDefault();
        const consentRow = privacyConsent.closest('.form-customer__consent');
        const errorMessage = consentRow?.querySelector('.field__message');
        consentRow?.classList.add('has-error');
        privacyConsent.setAttribute('aria-invalid', 'true');
        errorMessage?.classList.remove('hidden');
        errorMessage?.setAttribute('role', 'alert');
        privacyConsent.focus();
        return;
      }

      if (birthDateNoteInput) {
        const birthDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(birthDateInput?.value || '');
        birthDateNoteInput.value = birthDateMatch
          ? `dob:${birthDateMatch[3]}-${birthDateMatch[2]}-${birthDateMatch[1]}`
          : '';
      }

    });

    registerForm.querySelector('button[type="submit"]')?.addEventListener('click', (event) => {
      const validation = validateAllRegisterFields();
      if (!validation.valid) {
        event.preventDefault();
        validation.firstInvalid?.focus();
      }
    });

    emailInput?.addEventListener('input', () => {
      if (emailInput.value.trim()) validateEmailField();
    });
    emailInput?.addEventListener('blur', () => {
      if (emailInput.value.trim()) validateEmailField();
    });
    passwordInput?.addEventListener('input', () => {
      if (passwordInput.value) validatePasswordField();
    });
    passwordInput?.addEventListener('blur', () => {
      if (passwordInput.value) validatePasswordField();
    });
    [firstNameInput, lastNameInput].forEach((input) => {
      input?.addEventListener('input', () => {
        if (input.value.trim()) setFieldError(input, '');
      });
      input?.addEventListener('blur', () => {
        if (input.value.trim()) setFieldError(input, '');
      });
    });
    phoneInput?.addEventListener('input', validatePhoneField);
    phoneInput?.addEventListener('blur', validatePhoneField);
    birthDateInput?.addEventListener('input', () => {
      if (isValidBirthDate(birthDateInput.value)) setFieldError(birthDateInput, '');
    });
    birthDateInput?.addEventListener('blur', validateBirthDateField);

    const privacyConsent = this.steps.register.querySelector('[data-required-consent] input');
    privacyConsent?.addEventListener('change', () => {
      const consentRow = privacyConsent.closest('.form-customer__consent');
      const errorMessage = consentRow?.querySelector('.field__message');
      consentRow?.classList.remove('has-error');
      privacyConsent.setAttribute('aria-invalid', 'false');
      errorMessage?.classList.add('hidden');
    });
  }

  /* ----------------------------------------
     Password hints (register, reset-password, activate-account steps)
  ---------------------------------------- */
  _bindPasswordHints() {
    // Each step binds its own password field + hints container.
    const sources = [
      { step: 'register', inputId: 'RegisterPassword' },
      { step: 'reset-password', inputId: 'newPassword' },
      { step: 'activate-account', inputId: 'activePassword' },
    ];

    sources.forEach(({ step, inputId }) => {
      const stepEl = this.steps[step];
      const passwordField = stepEl && stepEl.querySelector('#' + inputId);
      if (!passwordField) return;

      const hintsRow = stepEl.querySelector('[data-password-hints]');
      if (!hintsRow) return;

      const hints = {
        length: hintsRow.querySelector('[data-hint="length"]'),
        letter: hintsRow.querySelector('[data-hint="letter"]'),
        number: hintsRow.querySelector('[data-hint="number"]'),
        match: hintsRow.querySelector('[data-hint="match"]'),
      };

      // En reset-password existe además el campo de confirmación.
      const confirmField = stepEl.querySelector('#confirmPassword');

      const evaluate = () => {
        const val = passwordField.value;
        this._setHint(hints.length, val.length >= 8 && val.length <= 30);
        this._setHint(hints.letter, /[a-zA-Z]/.test(val));
        this._setHint(hints.number, /[0-9]/.test(val));
        if (hints.match && confirmField) {
          const match = val.length > 0 && val === confirmField.value;
          this._setHint(hints.match, match);
        }
      };

      passwordField.addEventListener('input', evaluate);
      if (confirmField) {
        confirmField.addEventListener('input', evaluate);
      }

      // Bloquear submit si las contraseñas no coinciden (sólo reset-password).
      if (confirmField && hints.match) {
        const form = stepEl.querySelector('form');
        if (form) {
          form.addEventListener('submit', (event) => {
            if (passwordField.value !== confirmField.value) {
              event.preventDefault();
              confirmField
                .closest('[data-input-wrapper]')
                ?.classList.add('has-error');
              this._setHint(hints.match, false);
              confirmField.focus();
            }
          });
        }
      }
    });
  }

  _setHint(el, valid) {
    if (!el) return;
    el.classList.toggle('form-customer__password-hint--valid', valid);
  }

  /* ----------------------------------------
     Password visibility toggle
  ---------------------------------------- */
  _initPasswordToggles() {
    const HIDE_PASSWORD_ICON_URL = 'https://cdn.shopify.com/s/files/1/0723/5321/8835/files/icon_hide_password.svg?v=1790765267';
    const EYE_SVG = `<svg width="1em" height="1em" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M9.5235 4.79973C6.76257 4.92905 4.08307 6.62063 1.1722 9.66543C0.993412 9.85244 0.993412 10.1474 1.1722 10.3344C4.08307 13.3793 6.76258 15.0709 9.52351 15.2003C12.2733 15.3291 15.2667 13.9138 18.8217 10.3399C19.0086 10.152 19.0086 9.84814 18.8217 9.6602C15.2667 6.0863 12.2733 4.67093 9.5235 4.79973ZM9.47509 3.7592C12.6521 3.61039 15.9149 5.26347 19.5564 8.92433C20.1479 9.5189 20.1479 10.4812 19.5564 11.0758C15.9149 14.7366 12.6521 16.3897 9.47508 16.2408C6.30917 16.0924 3.3912 14.1603 0.42305 11.0555C-0.141017 10.4655 -0.141017 9.53435 0.423051 8.94433C3.3912 5.8396 6.30918 3.90749 9.47509 3.7592Z"/>
      <path d="M13.5807 10.0002C13.5807 11.9741 11.9742 13.5586 10.012 13.5586C8.04979 13.5586 6.44327 11.9741 6.44327 10.0002C6.44327 8.02617 8.04979 6.44176 10.012 6.44176C11.9742 6.44176 13.5807 8.02617 13.5807 10.0002ZM10.012 12.5169C11.4096 12.5169 12.5426 11.3901 12.5426 10.0002C12.5426 8.6102 11.4096 7.48342 10.012 7.48342C8.61438 7.48342 7.48138 8.6102 7.48138 10.0002C7.48138 11.3901 8.61438 12.5169 10.012 12.5169Z"/>
    </svg>`;

    this.container.querySelectorAll('[type="password"]').forEach((input) => {
      const wrapper = input.closest('[data-input-wrapper]');
      if (!wrapper || wrapper.querySelector('.field__password-toggle')) return;

      // Crear wrapper relativo alrededor del input para anclar el ojo
      // al input (no al .field). Así el ojo no se mueve cuando aparece
      // el mensaje de error.
      let inputWrapper = input.closest('.field__input-wrapper');
      if (!inputWrapper) {
        inputWrapper = document.createElement('div');
        inputWrapper.className = 'field__input-wrapper';
        input.parentNode.insertBefore(inputWrapper, input);
        inputWrapper.appendChild(input);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'field__password-toggle';
      btn.setAttribute('aria-label', customerLoginText('showPassword'));
      btn.innerHTML = EYE_SVG;

      inputWrapper.appendChild(btn);

      btn.addEventListener('click', () => {
        const isPassword = input.type === 'password';
        input.type = isPassword ? 'text' : 'password';
        btn.setAttribute('aria-label', isPassword ? customerLoginText('hidePassword') : customerLoginText('showPassword'));
        btn.innerHTML = isPassword
          ? `<img src="${HIDE_PASSWORD_ICON_URL}" alt="" aria-hidden="true">`
          : EYE_SVG;
        btn.classList.toggle('field__password-toggle--visible', isPassword);
      });
    });
  }

  /* ----------------------------------------
     Helpers
  ---------------------------------------- */
  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async _createGuestOrderHash(value) {
    if (window.crypto?.subtle && window.TextEncoder) {
      const encodedValue = new TextEncoder().encode(value);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', encodedValue);
      return Array.from(new Uint8Array(hashBuffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    return btoa(encodeURIComponent(value)).replace(/=+$/g, '');
  }

  _clearApiError() {
    const err = this.container.querySelector('.form-customer__api-error');
    if (err) err.remove();
  }
}

/* Auto-init when DOM is ready */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomerLogin);
} else {
  initCustomerLogin();
}

function initCustomerLogin() {
  const container = document.querySelector('[data-customer-login]');
  if (container) new CustomerLoginSteps(container);
}
